import { useEffect, useState } from 'react';
import { notification } from 'antd';
import { I18n } from '@aws-amplify/core';

import type { CycleCallback } from '@/components/ui/Timeline';
import type { BeraReportV2 } from '@/types/Bera';

import { findOverlappingStretches } from '@/components/views/BeraJob/BeraReportV2/Timeline/helpers';
import type { StretchRangeInSeconds } from '@/components/views/BeraJob/BeraReportV2/Timeline/helpers';
import { SAVE_RESULT } from './useStretchPersistence';
import type { RulerEditsParams, SaveResult } from './useStretchPersistence';

interface UseRulerEditingParams {
	rulerEdits: Record<string, CycleCallback>;
	rulerStretches: CycleCallback[];
	selectedReport?: BeraReportV2;
	clearRulerEdits: () => void;
	closeEditor: () => void;
	saveRulerEdits: (params: RulerEditsParams) => Promise<SaveResult>;
	onSaved?: () => void;
}

export function useRulerEditing({
	rulerEdits,
	rulerStretches,
	selectedReport,
	clearRulerEdits,
	closeEditor,
	saveRulerEdits,
	onSaved
}: UseRulerEditingParams) {
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [editableStretchIds, setEditableStretchIds] = useState<string[]>([]);

	useEffect(() => {
		if (!selectedReport || isEditMode) {
			return;
		}

		const segmentIds = (selectedReport.segments ?? []).map((segment) => segment.id);

		setEditableStretchIds(segmentIds);
	}, [selectedReport, isEditMode]);

	function notifyOverlappingStretches(conflict: StretchRangeInSeconds[]): void {
		const [first, second] = conflict;
		const times = `${first.start.toFixed(3)}s-${first.end.toFixed(3)}s / ${second.start.toFixed(3)}s-${second.end.toFixed(3)}s`;

		notification.warning({
			message: I18n.get('Overlapping stretches'),
			description: `${I18n.get('Adjust the overlapping stretches before editing all of them')}: ${times}`
		});
	}

	function exitEditMode(): void {
		setIsEditMode(false);

		setEditableStretchIds([]);

		clearRulerEdits();

		closeEditor();
	}

	function startEditing(): void {
		const conflict = findOverlappingStretches(rulerStretches);

		if (conflict) {
			notifyOverlappingStretches(conflict);
			return;
		}

		setIsEditMode(true);

		setEditableStretchIds(rulerStretches.map((stretch) => stretch.id));
	}

	async function finishEditing(): Promise<void> {
		const result = await saveRulerEdits({ rulerEdits, rulerStretches });

		if (result === SAVE_RESULT.FAILED) {
			return;
		}

		exitEditMode();

		if (result === SAVE_RESULT.SAVED) {
			onSaved?.();
		}
	}

	async function toggleEditMode(): Promise<void> {
		if (isEditMode) {
			await finishEditing();
			return;
		}

		startEditing();
	}

	function selectStretches(cycleIds: string[]): void {
		setEditableStretchIds(cycleIds);
	}

	function clearSelection(): void {
		setEditableStretchIds([]);
	}

	return {
		isEditMode,
		editableStretchIds,
		toggleEditMode,
		cancelEditMode: exitEditMode,
		selectStretches,
		clearSelection
	};
}
